import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import "../style/Export.css";
import { userApiBase } from "../config/urls";

/** Higher scale improves small SVG / icon clarity in html2canvas output */
const HTML2CANVAS_EXPORT_SCALE = 3;

// Retrieve user's subscription plan from localStorage
const getUserPlanFromStorage = (): 'free' | 'pro' | 'premium' => {
  const userPlan = localStorage.getItem('userPlan');
  if (userPlan === 'pro' || userPlan === 'premium') {
    return userPlan as 'free' | 'pro' | 'premium';
  }
  return 'free';
};

// Check if user has permission to export (Pro/Premium only)
const hasExportAccess = (): boolean => {
  const userPlan = getUserPlanFromStorage();
  const hasAccess = userPlan === 'pro' || userPlan === 'premium';
  
  console.log('🔒 Export Access Check:', {
    userPlan,
    hasAccess,
    storedPlan: localStorage.getItem('userPlan'),
    storedPlanId: localStorage.getItem('planId')
  });
  
  return hasAccess;
};

// Modal for free users prompting upgrade to access export
const showSubscriptionRequiredModal = (onUpgradeClick?: () => void): Promise<void> => {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'export-modal-overlay';
    
    const modal = document.createElement('div');
    modal.className = 'export-modal subscription-modal';
    
    modal.innerHTML = `
      <div class="subscription-required-content">
        <div class="lock-icon">🔒</div>
        <h3>Premium Feature</h3>
        <p>Export functionality is available for Pro and Premium subscribers only.</p>
        <div class="feature-benefits">
          <div class="benefit-item">✓ Export as PNG</div>
          <div class="benefit-item">✓ Export as PDF</div>
          <div class="benefit-item">✓ High-quality output</div>
        </div>
        <div class="export-modal-actions">
          <button class="export-modal-btn upgrade-btn">Upgrade Now</button>
          <button class="export-modal-btn cancel">Maybe Later</button>
        </div>
      </div>
    `;
    
    
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    
    modal.querySelector('.upgrade-btn')?.addEventListener('click', () => {
      document.body.removeChild(overlay);
      if (onUpgradeClick) {
        onUpgradeClick(); // Navigate to /subscription
      }
      resolve();
    });
    
    // Navigate to subscription page on upgrade click
    modal.querySelector('.cancel')?.addEventListener('click', () => {
      document.body.removeChild(overlay);
      resolve();
    });
    
    // Close on overlay click
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        document.body.removeChild(overlay);
        resolve();
      }
    });
  });
};

type ExportModalResult = {
  format: string;
  action: "export" | "share";
};

// Modal for users to choose png or pdf
const showExportModal = (): Promise<ExportModalResult | null> => {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'export-modal-overlay';
    
    const modal = document.createElement('div');
    modal.className = 'export-modal';
    
    let selectedFormat = 'png';
    
    modal.innerHTML = `
      <h3>Export Diagram</h3>
      <p>Choose your preferred export format</p>
      <div class="export-format-buttons">
        <button class="export-format-btn selected" data-format="png">
          <div>📄 PNG</div>
          <small style="color: inherit; opacity: 0.8;">Image file</small>
        </button>
        <button class="export-format-btn" data-format="pdf">
          <div>📋 PDF</div>
          <small style="color: inherit; opacity: 0.8;">Document file</small>
        </button>
      </div>
      <div class="export-modal-actions">
        <button class="export-modal-btn cancel">Cancel</button>
        <button class="export-modal-btn export">Export</button>
        <button class="export-modal-btn share">Share</button>
      </div>
    `;
    
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
    
    // Handle format selection
    const formatButtons = modal.querySelectorAll('.export-format-btn');
    formatButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        formatButtons.forEach(b => b.classList.remove('selected'));
        btn.classList.add('selected');
        selectedFormat = btn.getAttribute('data-format') || 'png';
      });
    });
    
    modal.querySelector('.cancel')?.addEventListener('click', () => {
      document.body.removeChild(overlay);
      resolve(null);
    });
    
    modal.querySelector('.export')?.addEventListener('click', () => {
      document.body.removeChild(overlay);
      resolve({ format: selectedFormat, action: "export" });
    });

    modal.querySelector('.share')?.addEventListener('click', () => {
      document.body.removeChild(overlay);
      resolve({ format: selectedFormat, action: "share" });
    });
    
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        document.body.removeChild(overlay);
        resolve(null);
      }
    });
  });
};

const getLoggedInUserEmail = (): string => {
  const user = localStorage.getItem("user");

  if (user) {
    try {
      const parsedUser = JSON.parse(user);
      return parsedUser.email || "";
    } catch {
      return "";
    }
  }

  return localStorage.getItem("email") || localStorage.getItem("userEmail") || "";
};

const showShareEmailModal = (
  senderEmail: string
): Promise<{ recipientEmail: string; message: string } | null> => {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "export-modal-overlay";

    const modal = document.createElement("div");
    modal.className = "export-modal";

    modal.innerHTML = `
      <h3>Share Diagram</h3>
      <p>Send this diagram directly by email.</p>

      <div class="share-from-text">
        <strong>From:</strong> ${senderEmail || "Logged-in user"}
      </div>

      <input class="share-email-input" type="email" placeholder="Recipient email" />

      <textarea class="share-message-input" placeholder="Message optional">Hi,

Please find attached the Impact Alignment Map.

Regards,</textarea>

      <div class="export-modal-actions">
        <button class="export-modal-btn cancel">Cancel</button>
        <button class="export-modal-btn share">Send Email</button>
      </div>
    `;

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    const removeModal = () => {
      if (document.body.contains(overlay)) {
        document.body.removeChild(overlay);
      }
    };

    modal.querySelector(".cancel")?.addEventListener("click", () => {
      removeModal();
      resolve(null);
    });

    modal.querySelector(".share")?.addEventListener("click", () => {
      const emailInput = modal.querySelector(".share-email-input") as HTMLInputElement;
      const messageInput = modal.querySelector(".share-message-input") as HTMLTextAreaElement;

      if (!emailInput.value.trim()) {
        alert("Please enter recipient email.");
        return;
      }

      removeModal();

      resolve({
        recipientEmail: emailInput.value.trim(),
        message: messageInput.value.trim(),
      });
    });
  });
};

/** Encode file as base64 for JSON POST (avoids multipart stream issues on Vercel serverless). */
const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Could not read file"));
        return;
      }
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error("File read failed"));
    reader.readAsDataURL(file);
  });

const sendDiagramEmail = async (
  file: File,
  senderEmail: string,
  recipientEmail: string,
  message: string
) => {
  const apiBase = userApiBase();
  const fileBase64 = await fileToBase64(file);

  const response = await fetch(`${apiBase.replace(/\/$/, "")}/api/shareDiagram`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      fileBase64,
      fileName: file.name,
      mimeType: file.type || "application/octet-stream",
      senderEmail,
      recipientEmail,
      message,
    }),
  });

  if (!response.ok) {
    let detail = "Failed to send email";
    try {
      const data = await response.json();
      detail =
        (data && (data.message || data.error)) || JSON.stringify(data);
    } catch {
      detail = (await response.text()) || detail;
    }
    console.error("Email share backend error:", detail);
    throw new Error(detail);
  }
};

// Main export function - handles permission check, UI preparation, and export
export const exportVisualDiagram = async (
  element: HTMLElement, 
  filename: string = 'theory-of-change',
  onUpgradeClick?: () => void
) => {
  try {
    
    // Check export permission - redirect free users to upgrade
    if (!hasExportAccess()) {
      await showSubscriptionRequiredModal(onUpgradeClick);
      return; // Stop execution if user doesn't have access
    }
    
    // Get user's format preference
    const modalResult = await showExportModal();

    if (!modalResult) {
      return;
    }

    const { format, action } = modalResult;

    // Prepare element for export - add export mode class
    element.classList.add('export-mode');

   // Hide sidebar during export
    const sidebar = document.querySelector('.sidebar') as HTMLElement;
    const originalSidebarDisplay = sidebar ? sidebar.style.display : '';
    if (sidebar) {
      sidebar.style.display = 'none';
    }

    // Hide all interactive controls (buttons, color pickers)
    const customizeControls = element.querySelectorAll(
      '.customize-controls-column, .cloud-customize-controls, .add-card-btn, .remove-card-btn, .add-cloud-btn, .remove-cloud-btn, .cloud-buttons, .add-remove-wrapper'
    );
    customizeControls.forEach((el) => {
      (el as HTMLElement).style.display = 'none';
    });

    // Store original styles to restore after export
    const originalStyles: Array<{
      el: HTMLElement;
      height: string;
      minHeight: string;
      maxHeight: string;
      overflow: string;
      whiteSpace: string;
    }> = [];

    // Force all containers to expand fully (prevent content clipping)
    const containers = element.querySelectorAll(
      '.flow-card, .influence-cloud, .card-container, .outer-card, .card-value, .cloud-value, .flow-row'
    );
    
    containers.forEach((container) => {
      const el = container as HTMLElement;
      originalStyles.push({
        el: el,
        height: el.style.height,
        minHeight: el.style.minHeight,
        maxHeight: el.style.maxHeight,
        overflow: el.style.overflow,
        whiteSpace: el.style.whiteSpace
      });
      
      // Remove height constraints and show all content
      el.style.height = 'auto';
      el.style.minHeight = 'auto';
      el.style.maxHeight = 'none';
      el.style.overflow = 'visible'; 
      el.style.whiteSpace = 'normal';
    });
    
    // ensure any wrapper divs don't clip content
    const allDivs = element.querySelectorAll('div');
    const divOriginalStyles: Array<{ el: HTMLElement; overflow: string }> = [];
    allDivs.forEach((div) => {
      const el = div as HTMLElement;
      divOriginalStyles.push({
        el: el,
        overflow: el.style.overflow
      });
     // Force visible overflow for all divs
      if (el.style.overflow === 'hidden' || el.style.overflow === 'auto' || el.style.overflow === 'scroll') {
        el.style.overflow = 'visible';
      }
    });

    // Replace textareas with divs for proper rendering in canvas
    const allTextareas = element.querySelectorAll('textarea');
    const originalTextareaStyles: Array<{
      el: HTMLTextAreaElement;
      height: string;
      minHeight: string;
      maxHeight: string;
      overflow: string;
      resize: string;
      display: string;
    }> = [];
    
    // Store textarea replacements for later cleanup
    const textareaReplacements: Array<{ textarea: HTMLTextAreaElement; replacement: HTMLDivElement }> = [];

    allTextareas.forEach((textarea) => {
      originalTextareaStyles.push({
        el: textarea,
        height: textarea.style.height,
        minHeight: textarea.style.minHeight,
        maxHeight: textarea.style.maxHeight,
        overflow: textarea.style.overflow,
        resize: textarea.style.resize,
        display: textarea.style.display
      });
      
      // Create div that mimics textarea styling
      // Create div that mimics textarea styling
const replacement = document.createElement('div');
replacement.className = 'textarea-replacement';
replacement.textContent = textarea.value;

const computedStyle = window.getComputedStyle(textarea);
const isImpactCardTextarea = textarea.classList.contains('impact-card-textarea');

replacement.style.cssText = textarea.style.cssText;
replacement.style.width = computedStyle.width;
replacement.style.whiteSpace = 'pre-wrap';
replacement.style.wordWrap = 'break-word';
replacement.style.wordBreak = 'break-word';
replacement.style.fontFamily = computedStyle.fontFamily;
replacement.style.fontSize = computedStyle.fontSize;
replacement.style.fontWeight = computedStyle.fontWeight;
replacement.style.lineHeight = isImpactCardTextarea ? '1.25' : computedStyle.lineHeight;
replacement.style.padding = computedStyle.padding;
replacement.style.border = computedStyle.border;
replacement.style.borderRadius = computedStyle.borderRadius;
replacement.style.backgroundColor = computedStyle.backgroundColor;
replacement.style.color = computedStyle.color;
replacement.style.boxSizing = 'border-box';
replacement.style.textAlign = computedStyle.textAlign;

if (isImpactCardTextarea) {
  replacement.style.height = computedStyle.height;
  replacement.style.minHeight = '0';
  replacement.style.maxHeight = computedStyle.height;
  replacement.style.overflow = 'hidden';
} else {
  replacement.style.height = 'auto';
  replacement.style.minHeight = '0';
  replacement.style.maxHeight = 'none';
  replacement.style.overflow = 'visible';
}
      
     // Hide original textarea and insert replacement
      textarea.style.display = 'none';
      textarea.parentNode?.insertBefore(replacement, textarea);
      
    if (isImpactCardTextarea) {
  let fontSize = parseFloat(window.getComputedStyle(replacement).fontSize) || 14;

  while (
    replacement.scrollHeight > replacement.clientHeight &&
    fontSize > 8
  ) {
    fontSize -= 0.5;
    replacement.style.fontSize = `${fontSize}px`;
  }
}

      textareaReplacements.push({ textarea, replacement });
    });

    // Wait for browser layout to settle before capturing
    await new Promise(resolve => setTimeout(resolve, 400));
    
    // Second pass: Re-verify textarea heights after layout
    allTextareas.forEach((textarea) => {
  if (textarea.classList.contains('impact-card-textarea')) return;

  const computedStyle = window.getComputedStyle(textarea);
      const width = computedStyle.width;
      textarea.style.width = width;
      
      // Reset height to recalculate
      textarea.style.height = 'auto';
      void textarea.offsetHeight;
      void textarea.scrollHeight;
      const finalHeight = textarea.scrollHeight;
      textarea.style.height = (finalHeight + 8) + 'px';
    });
    
    await new Promise(resolve => setTimeout(resolve, 400));
    
    // Third pass: Final height verification
   allTextareas.forEach((textarea) => {
  if (textarea.classList.contains('impact-card-textarea')) return;

  void textarea.offsetHeight;
      const verifyHeight = textarea.scrollHeight;
      if (parseInt(textarea.style.height) < verifyHeight) {
        textarea.style.height = (verifyHeight + 8) + 'px';
      }
    });
    
    await new Promise(resolve => setTimeout(resolve, 200));

    // Capture element as canvas using html2canvas
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      useCORS: true,
      scale: HTML2CANVAS_EXPORT_SCALE,
      logging: false,
      width: element.scrollWidth,
      height: element.scrollHeight,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
      scrollX: 0,
      scrollY: 0,
      onclone: (clonedDoc) => {
        // Additional fixes in the cloned document
        const clonedElement = clonedDoc.querySelector('.export-mode');
        if (clonedElement) {
          // Force expansion in cloned document
          const clonedContainers = clonedElement.querySelectorAll(
            '.flow-card, .influence-cloud, .card-container, .outer-card, .card-value, .cloud-value'
          );
          clonedContainers.forEach((el) => {
            const htmlEl = el as HTMLElement;
            htmlEl.style.height = 'auto';
            htmlEl.style.minHeight = 'auto';
            htmlEl.style.maxHeight = 'none';
            htmlEl.style.overflow = 'visible';
          });

          // Stabilize Lucide/SVG footprint icons for canvas rasterization
          clonedElement
            .querySelectorAll('.footstep-group svg, .legend-footprints svg')
            .forEach((svg) => {
              const node = svg as SVGSVGElement;
              node.setAttribute('shape-rendering', 'geometricPrecision');
            });

          // Force textarea expansion in clone with multiple reflow passes
          const clonedTextareas = clonedElement.querySelectorAll('textarea');
          clonedTextareas.forEach((textarea) => {
            const ta = textarea as HTMLTextAreaElement;
            
            const computedStyle = window.getComputedStyle(ta);
            const width = computedStyle.width;
            
            ta.style.height = 'auto';
            ta.style.minHeight = '0';
            ta.style.maxHeight = 'none';
            ta.style.overflow = 'hidden';
            ta.style.resize = 'none';
            ta.style.whiteSpace = 'pre-wrap';
            ta.style.wordWrap = 'break-word';
            ta.style.boxSizing = 'border-box';
            ta.style.width = width;
            
            // Multiple reflow passes for accurate height calculation
            void ta.offsetHeight;
            void ta.scrollHeight;
            let scrollHeight = ta.scrollHeight;
            ta.style.height = (scrollHeight + 8) + 'px';
            void ta.offsetHeight;
            scrollHeight = ta.scrollHeight;
            ta.style.height = (scrollHeight + 8) + 'px';
            void ta.offsetHeight;
            scrollHeight = ta.scrollHeight;
            ta.style.height = (scrollHeight + 8) + 'px';
          });
        }
      }
    });

    // Restore all original styles
    originalStyles.forEach(({ el, height, minHeight, maxHeight, overflow }) => {
      el.style.height = height;
      el.style.minHeight = minHeight;
      el.style.maxHeight = maxHeight;
      el.style.overflow = overflow;
    });

    divOriginalStyles.forEach(({ el, overflow }) => {
      el.style.overflow = overflow;
    });

    originalTextareaStyles.forEach(({ el, height, minHeight, maxHeight, overflow, resize }) => {
      el.style.height = height;
      el.style.minHeight = minHeight;
      el.style.maxHeight = maxHeight;
      el.style.overflow = overflow;
      el.style.resize = resize;
    });

    // Remove export mode class
    element.classList.remove('export-mode');

    // Restore sidebar visibility
    if (sidebar) {
      sidebar.style.display = originalSidebarDisplay;
    }

    // Restore controls visibility
    customizeControls.forEach((el) => {
      (el as HTMLElement).style.display = '';
    });
    
    // Cleanup textarea replacements
    textareaReplacements.forEach(({ textarea, replacement }) => {
      replacement.remove();
      textarea.style.display = '';
    });
    
    // Export or share based on selected format
    const senderEmail = getLoggedInUserEmail();

    if (format.toLowerCase() === "pdf") {
      const imgData = canvas.toDataURL("image/png", 1.0);

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const a4Width = 595.28;
      const a4Height = 841.89;

      const orientation = imgWidth > imgHeight ? "landscape" : "portrait";
      const pageWidth = orientation === "landscape" ? a4Height : a4Width;
      const pageHeight = orientation === "landscape" ? a4Width : a4Height;

      const padding = 20;
      const availableWidth = pageWidth - 2 * padding;
      const availableHeight = pageHeight - 2 * padding;

      const scale = Math.min(availableWidth / imgWidth, availableHeight / imgHeight);

      const scaledWidth = imgWidth * scale;
      const scaledHeight = imgHeight * scale;

      const x = (pageWidth - scaledWidth) / 2;
      const y = (pageHeight - scaledHeight) / 2;

      const pdf = new jsPDF({
        orientation,
        unit: "pt",
        format: "a4",
        compress: true,
      });

      pdf.addImage(imgData, "PNG", x, y, scaledWidth, scaledHeight, undefined, "SLOW");

      if (action === "share") {
        const shareDetails = await showShareEmailModal(senderEmail);

        if (!shareDetails) {
          return;
        }

        const pdfBlob = pdf.output("blob");

        const file = new File([pdfBlob], `${filename}.pdf`, {
          type: "application/pdf",
        });

        await sendDiagramEmail(
          file,
          senderEmail,
          shareDetails.recipientEmail,
          shareDetails.message
        );

        alert("Diagram shared successfully by email.");
      } else {
        pdf.save(`${filename}.pdf`);
      }
    } else {
      if (action === "share") {
        const shareDetails = await showShareEmailModal(senderEmail);

        if (!shareDetails) {
          return;
        }

        const pngBlob = await new Promise<Blob | null>((resolve) => {
          canvas.toBlob((blob) => resolve(blob), "image/png", 1.0);
        });

        if (!pngBlob) {
          alert("Unable to prepare PNG file.");
          return;
        }

        const file = new File([pngBlob], `${filename}.png`, {
          type: "image/png",
        });

        await sendDiagramEmail(
          file,
          senderEmail,
          shareDetails.recipientEmail,
          shareDetails.message
        );

        alert("Diagram shared successfully by email.");
      } else {
        const link = document.createElement("a");
        link.download = `${filename}.png`;
        link.href = canvas.toDataURL("image/png", 1.0);
        link.click();
      }
    }  
  } catch (error) {
    console.error('Export failed:', error);
    alert('Export failed. Please try again.');
    throw error;
  }
};